import React from 'react';
import HeaderTitle from './HeaderTitle';
import HeaderSubtitle from './HeaderSubtitle';
import { HeaderAccountSlot } from './HeaderAccount';

// The page header, for every top-level surface.
//
// WHY THIS EXISTS
// Create, Vault, Routine, Account, Explore and Home each hand-rolled the same
// two-layout header with slightly different class strings — one at 64px where
// the rest were 74px, one `font-light` and one `font-normal`, one missing the
// rule. The titles sat at a different height on every page, so moving between
// tabs made the whole shell jump. The design system primitives ARE the DRY
// abstraction (CLAUDE.md); this is the one that was missing.
//
//   desktop (lg+)                    mobile
//   ┌────────────────────────┐       ┌────────────────────────┐
//   │ TITLE          [right] │       │       T I T L E        │  centred,
//   │ subtitle, italic       │       │   subtitle, italic     │  wide tracking
//   │ ────                   │       │                        │
//   │ [desktopExtra]         │       │     [mobileExtra]      │
//   └────────────────────────┘       └────────────────────────┘
//
// ONE <h1>, RESPONSIVE — NOT two hidden by breakpoint. Rendering a desktop
// heading and a mobile heading puts two identical <h1>s in the DOM: invisible
// to users, both read by crawlers. That defect was already fixed once on the
// signed-out splash, and four of these pages were quietly carrying it. The two
// layouts differ only in alignment, tracking, weight and size, all of which a
// responsive class expresses.
//
// The subtitle is the one part that can differ in CONTENT between breakpoints
// (Create says less on a phone), so it is two elements only when the strings
// actually differ — otherwise it stays a single responsive element for the same
// reason as the heading.
//
// SIZES ARE FIXED, NOT PROPS. That is the point — a per-page size override is
// how the baselines drifted apart in the first place. A page needing a
// different composition (a back button, a dynamic title that would overflow at
// 74px) should compose its own header rather than widening this one.
//
// `pl-[0.3em]` on mobile offsets the trailing letter-space that 0.3em tracking
// adds after the final character; without it a centred title reads visibly left
// of centre.
//
// Props:
//   title           string — the page name, already in display case
//   subtitle        desktop subtitle
//   mobileSubtitle  mobile subtitle; omit to reuse `subtitle`, pass null to drop
//   desktopRight    control pinned to the top-right of the desktop layout
//   desktopExtra    node under the desktop rule (stage meters, toggles)
//   mobileExtra     node under the mobile subtitle (tab rows)
//   rule            the 72x2 divider under the desktop subtitle (default true)

const TITLE_CLASS = [
  'font-display text-bone leading-[1.2] lg:leading-none',
  'text-center lg:text-left',
  'font-normal lg:font-light',
  'tracking-[0.3em] lg:tracking-[0.12em]',
  'pl-[0.3em] lg:pl-0',
].join(' ');

const SUBTITLE_CLASS = 'font-display italic text-bone-dim mt-1.5 lg:mt-2.5 text-sm lg:text-[19px]';

const PageHeader = ({
  title,
  subtitle = null,
  mobileSubtitle,
  desktopRight = null,
  desktopExtra = null,
  mobileExtra = null,
  rule = true,
}) => {
  const mobileSub = mobileSubtitle === undefined ? subtitle : mobileSubtitle;
  const sameSubtitle = mobileSub === subtitle;

  return (
    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between lg:gap-8">
      <div className="min-w-0 text-center lg:text-left">
        {/* The title gets its own relative row so the account control can centre
            on the TITLE LINE rather than on the header block. Pinned to the top
            of the block it sat level with the title only by coincidence and
            dropped out of alignment the moment a subtitle rendered. The row's
            height IS the line box, so `top-1/2` tracks the type at every
            breakpoint and through the scroll-condense. */}
        <div className="relative">
          <HeaderTitle className={TITLE_CLASS} size="text-[27px] lg:text-[74px]" sizeScrolled="text-[20px] lg:text-[52px]">
            {title}
          </HeaderTitle>
          <HeaderAccountSlot />
        </div>

        {sameSubtitle
          ? subtitle && <HeaderSubtitle className={SUBTITLE_CLASS}>{subtitle}</HeaderSubtitle>
          : (
            <>
              {subtitle && (
                <HeaderSubtitle className={`hidden lg:block ${SUBTITLE_CLASS}`}>
                  {subtitle}
                </HeaderSubtitle>
              )}
              {mobileSub && (
                <HeaderSubtitle className={`lg:hidden ${SUBTITLE_CLASS}`}>
                  {mobileSub}
                </HeaderSubtitle>
              )}
            </>
          )}

        {rule && (
          <div className="hidden lg:block w-[72px] h-[2px] bg-[#3c3b38] mt-5" aria-hidden="true" />
        )}
        {desktopExtra && <div className="hidden lg:block">{desktopExtra}</div>}
        {mobileExtra && <div className="lg:hidden">{mobileExtra}</div>}
      </div>

      {desktopRight && <div className="hidden lg:block shrink-0">{desktopRight}</div>}
    </div>
  );
};

export default PageHeader;
